"use client";
import React, { useState } from "react";
import { X, MapPin, ChevronDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAddress } from "../../context/AddressContext";

const AddAddress = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addAddress } = useAddress();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    pin: "",
    city: "",
    state: "",
    countryCode: "+91",
  });

  const [isPrimary, setIsPrimary] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countryCodes = [
    { code: "+1", country: "USA" },
  ];

  // Validation functions
  const validateName = (name) => {
    const alphabetRegex = /^[A-Za-z\s]+$/;
    if (!name.trim()) {
      return "Full name is required";
    }
    if (!alphabetRegex.test(name)) {
      return "Name should contain only alphabets";
    }
    return "";
  };

  const validatePhone = (phone) => {
    const numericRegex = /^\d+$/;
    if (!phone.trim()) {
      return "Contact number is required";
    }
    if (!numericRegex.test(phone)) {
      return "Phone number should contain only digits";
    }
    if (phone.length !== 10) {
      return "Phone number must be exactly 10 digits";
    }
    return "";
  };

  const validateAddress = (address) => {
    if (!address.trim()) {
      return "Street address is required";
    }
    return "";
  };

  const validatePin = (pin) => {
    const numericRegex = /^\d+$/;
    if (pin && !numericRegex.test(pin)) {
      return "PIN should contain only digits";
    }
    return "";
  };

  // Handle input changes with validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;

    // Filter input based on field type
    if (name === "name") {
      // Allow only alphabets and spaces
      filteredValue = value.replace(/[^A-Za-z\s]/g, "");
    } else if (name === "phone") {
      // Allow only numeric input and limit to 10 digits
      filteredValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "pin") {
      // Allow only numeric input for PIN
      filteredValue = value.replace(/\D/g, "");
    }

    setFormData({ ...formData, [name]: filteredValue });

    // Validate the field only if it has been touched
    if (touched[name]) {
      let error = "";
      switch (name) {
        case "name":
          error = validateName(filteredValue);
          break;
        case "phone":
          error = validatePhone(filteredValue);
          break;
        case "address":
          error = validateAddress(filteredValue);
          break;
        case "pin":
          error = validatePin(filteredValue);
          break;
        default:
          break;
      }

      setErrors({ ...errors, [name]: error });
    }
  };

  // Handle field blur (when user leaves a field)
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });

    // Validate the field when user leaves it
    let error = "";
    switch (name) {
      case "name":
        error = validateName(formData[name]);
        break;
      case "phone":
        error = validatePhone(formData[name]);
        break;
      case "address":
        error = validateAddress(formData[name]);
        break;
      case "pin":
        error = validatePin(formData[name]);
        break;
      default:
        break;
    }

    setErrors({ ...errors, [name]: error });
  };

  // Check if form is valid
  const isFormValid = () => {
    const nameError = validateName(formData.name);
    const phoneError = validatePhone(formData.phone);
    const addressError = validateAddress(formData.address);
    const pinError = validatePin(formData.pin);

    const hasErrors = nameError || phoneError || addressError || pinError;
    const hasRequiredFields = formData.name && formData.phone && formData.address;

    return !hasErrors && hasRequiredFields;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Final validation check
    const nameError = validateName(formData.name);
    const phoneError = validatePhone(formData.phone);
    const addressError = validateAddress(formData.address);
    const pinError = validatePin(formData.pin);

    if (nameError || phoneError || addressError || pinError) {
      setErrors({
        name: nameError,
        phone: phoneError,
        address: addressError,
        pin: pinError,
      });
      setTouched({
        name: true,
        phone: true,
        address: true,
        pin: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare address data for the context
      const addressData = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        pin: formData.pin,
        state: formData.state,
        isPrimary: isPrimary,
      };

      // Add address using context and get the new address data
      const newAddress = await addAddress(addressData);

      // Check where to navigate back to
      const returnTo = location.state?.returnTo || "/cartsection";
      const fromCart = location.state?.fromCart;

      if (fromCart) {
        // Navigate back to cart and reload the page to refresh addresses
        navigate(returnTo, { replace: true });
        window.location.reload();
      } else {
        // Default navigation
        navigate(returnTo, { replace: true });
      }
    } catch (error) {
      console.error("Error adding address:", error);
      alert("Failed to add address. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      const returnTo = location.state?.returnTo || "/cartsection";
      navigate(returnTo, { replace: true });
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 relative max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded-full p-1"
          aria-label="Close add address form"
        >
          <X size={24} />
        </button>

        {/* Title */}
        <h2 className="text-lg font-semibold text-center mb-6 text-gray-800">Add Address</h2>

        {/* Form */}
        <form onSubmit={handleSave} className="flex flex-col gap-4" noValidate>
          {/* Full Name */}
          <div className="flex flex-col">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Full Name"
              className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 ${
                touched.name && errors.name ? "border-red-500" : "border-gray-300"
              }`}
              aria-label="Full Name"
              aria-describedby={touched.name && errors.name ? "name-error" : undefined}
              aria-invalid={!!(touched.name && errors.name)}
              required
            />
            {touched.name && errors.name && (
              <span
                id="name-error"
                className="text-red-500 text-sm mt-1"
                role="alert"
              >
                {errors.name}
              </span>
            )}
          </div>

          {/* Contact Number */}
          <div className="flex flex-col">
            <div className={`flex items-center gap-2 border rounded-md ${
              touched.phone && errors.phone ? "border-red-500" : "border-gray-300"
            }`}>
              {/* <select
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                className="border-r border-gray-300 p-2 rounded-l-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
                aria-label="Country Code"
              >
                {countryCodes.map((c, index) => (
                  <option key={index} value={c.code}>
                    {c.country} ({c.code})
                  </option>
                ))}
              </select> */}
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Contact Number"
                className="flex-1 p-2 outline-none rounded-r-md"
                aria-label="Contact Number"
                aria-describedby={touched.phone && errors.phone ? "phone-error" : undefined}
                aria-invalid={!!(touched.phone && errors.phone)}
                maxLength="10"
                required
              />
            </div>
            {touched.phone && errors.phone && (
              <span
                id="phone-error"
                className="text-red-500 text-sm mt-1"
                role="alert"
              >
                {errors.phone}
              </span>
            )}
          </div>

          {/* Street Address */}
          <div className="flex flex-col">
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Street Address"
              className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 ${
                touched.address && errors.address ? "border-red-500" : "border-gray-300"
              }`}
              aria-label="Street Address"
              aria-describedby={touched.address && errors.address ? "address-error" : undefined}
              aria-invalid={!!(touched.address && errors.address)}
              required
            />
            {touched.address && errors.address && (
              <span
                id="address-error"
                className="text-red-500 text-sm mt-1"
                role="alert"
              >
                {errors.address}
              </span>
            )}
          </div>

          {/* Drop Pin */}
          <div className="flex flex-col">
            <div className="flex items-center border border-gray-300 rounded-md px-3 py-2">
              <input
                type="text"
                name="pin"
                value={formData.pin}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Zip Code"
                className={`flex-1 outline-none ${
                  touched.pin && errors.pin ? "text-red-500" : ""
                }`}
                aria-label="Postal Code"
                aria-describedby={touched.pin && errors.pin ? "pin-error" : undefined}
                aria-invalid={!!(touched.pin && errors.pin)}
              />
              <MapPin className="text-red-500 ml-2" size={18} aria-hidden="true" />
            </div>
            {touched.pin && errors.pin && (
              <span
                id="pin-error"
                className="text-red-500 text-sm mt-1"
                role="alert"
              >
                {errors.pin}
              </span>
            )}
          </div>

          {/* Select City */}
          <div className="flex items-center border border-gray-300 rounded-md px-3 py-2">
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Select City"
              className="flex-1 outline-none"
              aria-label="City"
            />
            {/* <ChevronDown className="text-gray-500 ml-2" size={18} aria-hidden="true" /> */}
          </div>

          <div className="flex items-center border border-gray-300 rounded-md px-3 py-2">
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="Select State"
              className="flex-1 outline-none"
              aria-label="State"
            />
          </div>

          {/* Save as primary toggle */}
          <div className="flex items-center justify-between">
            <label
              htmlFor="primary-toggle"
              className="text-sm font-medium cursor-pointer"
            >
              Save as primary address
            </label>
            <button
              type="button"
              id="primary-toggle"
              onClick={() => setIsPrimary(!isPrimary)}
              className={`w-10 h-5 flex items-center rounded-full p-1 duration-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                isPrimary ? "bg-red-500" : "bg-gray-300"
              }`}
              aria-pressed={isPrimary}
              aria-label={`Set as primary address: ${isPrimary ? "enabled" : "disabled"}`}
            >
              <span
                className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${
                  isPrimary ? "translate-x-5" : "translate-x-0"
                }`}
                aria-hidden="true"
              />
            </button>
          </div>

          {/* Save Button */}
          <button
            type="submit"
            disabled={!isFormValid() || isSubmitting}
            className={`mt-4 py-2 rounded-lg font-medium transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isFormValid() && !isSubmitting
                ? "bg-green-500 text-white hover:bg-green-600 focus:ring-green-500 cursor-pointer"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
            aria-label="Save Address"
          >
            {isSubmitting ? "Saving..." : "Save Address"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddAddress;
